import { useEffect, useState } from "react";

import rawBreakpoints from "@root/tablescape/theme/breakpoints.json";

const breakpoints = Object.fromEntries(
  Object.entries(rawBreakpoints).map(([key, value]) => [key, parseInt(value)])
);

type BreakpointKey = keyof typeof rawBreakpoints;

/**
 * Hook to detect if the current screen width is below a specified breakpoint.
 *
 * Uses `requestAnimationFrame` for optimized resize event handling to prevent
 * excessive re-renders during window resizing.
 *
 * @param breakpointKey - The breakpoint to check against (sm, md, mg, lg, xl)
 * @returns true if the current screen width is below the specified breakpoint
 *
 * @example
 * ```tsx
 * const isMobile = useBreakpoint("md"); // true when screen < 768px
 * const isTablet = useBreakpoint("lg"); // true when screen < 1280px
 *
 * return (
 *   <div>
 *     {isMobile ? <MobileComponent /> : <DesktopComponent />}
 *   </div>
 * );
 * ```
 *
 * @example
 * ```tsx
 * const isBelowDesktop = useBreakpoint("mg"); // true when screen < 1024px
 *
 * return (
 *   <div className={isBelowDesktop ? "mobile-layout" : "desktop-layout"}>
 *     Content
 *   </div>
 * );
 * ```
 */
export function useBreakpoint(breakpointKey: BreakpointKey) {
  const breakpointValue = breakpoints[breakpointKey];

  const [isBelowBreakpoint, setIsBelowBreakpoint] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth <= breakpointValue;
  });

  useEffect(() => {
    // SSR guard: skip if window is not available
    if (typeof window === "undefined") {
      return;
    }

    let rafId: number | undefined;

    const checkScreen = () => {
      // Cancel any pending animation frame
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
        rafId = undefined;
      }

      // Schedule new check
      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        if (typeof window !== "undefined") {
          setIsBelowBreakpoint(window.innerWidth <= breakpointValue);
        }
      });
    };

    // Initial check
    setIsBelowBreakpoint(window.innerWidth <= breakpointValue);

    window.addEventListener("resize", checkScreen);

    return () => {
      window.removeEventListener("resize", checkScreen);
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
        rafId = undefined;
      }
    };
  }, [breakpointValue]);

  return isBelowBreakpoint;
}

/**
 * Hook to detect if the current screen is below mobile size.
 *
 * Uses the "sm" breakpoint (376px) as the mobile boundary.
 * This is more readable than `useBreakpoint("sm")` for mobile detection.
 *
 * @returns true if the current screen width is below 376px (mobile)
 *
 * @example
 * ```tsx
 * const isBelowMobile = useIsBelowMobile();
 *
 * return (
 *   <div>
 *     {isBelowMobile ? <MobileComponent /> : <TabletOrDesktopComponent />}
 *   </div>
 * );
 * ```
 */
export function useIsBelowMobile() {
  return useBreakpoint("sm");
}

/**
 * Hook to detect if the current screen is below tablet size.
 *
 * Uses the "md" breakpoint (768px) as the tablet boundary.
 * This is more readable than `useBreakpoint("md")` for tablet detection.
 *
 * @returns true if the current screen width is below 768px (mobile or tablet)
 *
 * @example
 * ```tsx
 * const isBelowTablet = useIsBelowTablet();
 *
 * return (
 *   <div>
 *     {isBelowTablet ? <MobileOrTabletComponent /> : <DesktopComponent />}
 *   </div>
 * );
 * ```
 */
export function useIsBelowTablet() {
  return useBreakpoint("md");
}

/**
 * Hook to detect if the current screen is below large tablet size.
 *
 * Uses the "mg" breakpoint (1024px) as the large tablet boundary.
 * This is more readable than `useBreakpoint("mg")` for large tablet detection.
 *
 * @returns true if the current screen width is below 1024px (mobile, tablet, or large tablet)
 *
 * @example
 * ```tsx
 * const isBelowLargeTablet = useIsBelowLargeTablet();
 *
 * return (
 *   <div>
 *     {isBelowLargeTablet ? <MobileLayout /> : <DesktopLayout />}
 *   </div>
 * );
 * ```
 */
export function useIsBelowLargeTablet() {
  return useBreakpoint("mg");
}

/**
 * Hook to detect if the current screen is below desktop size.
 *
 * Uses the "lg" breakpoint (1280px) as the desktop boundary.
 * This is more readable than `useBreakpoint("lg")` for desktop detection.
 *
 * @returns true if the current screen width is below 1280px (not large desktop)
 *
 * @example
 * ```tsx
 * const isBelowDesktop = useIsBelowDesktop();
 *
 * return (
 *   <div>
 *     {isBelowDesktop ? <CompactLayout /> : <WideLayout />}
 *   </div>
 * );
 * ```
 */
export function useIsBelowDesktop() {
  return useBreakpoint("lg");
}

/**
 * Hook to detect if the current screen is below large desktop size.
 *
 * Uses the "xl" breakpoint (1440px) as the large desktop boundary.
 * This is more readable than `useBreakpoint("xl")` for large desktop detection.
 *
 * @returns true if the current screen width is below 1440px (not extra large desktop)
 *
 * @example
 * ```tsx
 * const isBelowLargeDesktop = useIsBelowLargeDesktop();
 *
 * return (
 *   <div>
 *     {isBelowLargeDesktop ? <StandardLayout /> : <UltraWideLayout />}
 *   </div>
 * );
 * ```
 */
export function useIsBelowLargeDesktop() {
  return useBreakpoint("xl");
}
